import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { useSEO, getOrganizationSchema, getFAQSchema, getBreadcrumbSchema } from '../../utils/seo';

export default function Resources() {
  // SEO Configuration
  const faqs = [
    {
      question: 'How do I know if I qualify for home health services?',
      answer: 'Qualification typically depends on your medical condition, insurance coverage, and physician orders. Our intake coordinators can help determine your eligibility during a free consultation.'
    },
    {
      question: 'What is the difference between skilled and non-skilled care?',
      answer: 'Skilled care involves medical services provided by licensed nurses or therapists (wound care, IV therapy, physical therapy). Non-skilled care includes personal care assistance, companionship, and help with daily activities.'
    },
    {
      question: 'How quickly can services start?',
      answer: 'For routine referrals, services typically begin within 48 hours. Urgent cases can often be accommodated within 24 hours, and STAT referrals receive immediate attention.'
    },
    {
      question: 'Will insurance cover my home health services?',
      answer: 'Coverage varies by insurance type and specific services needed. Medicare, Medicaid, and most private insurances cover medically necessary home health services. We verify coverage before services begin.'
    },
    {
      question: 'Can I choose my caregiver or nurse?',
      answer: "We work to match you with caregivers who meet your preferences and needs. If you're not satisfied with your assigned caregiver, we can arrange for a replacement."
    },
    {
      question: 'What if I need care outside of normal business hours?',
      answer: 'Our agencies provide 24/7 on-call services for emergencies. Some agencies also offer overnight care and weekend services based on your care plan.'
    }
  ];

  useSEO({
    title: 'Healthcare Resources & Support - The Villarreal Health Network',
    description: 'Essential healthcare guides, tools, and information for home health services. Educational materials for patients, families, and caregivers in Las Vegas, Nevada.',
    keywords: 'healthcare resources, home health guides, patient education, caregiver support, insurance information, care planning resources',
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "name": "Healthcare Resources & Support",
          "description": "Educational resources and support materials for home healthcare",
          "provider": getOrganizationSchema()
        },
        getFAQSchema(faqs),
        getBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Resources', url: '/resources' }
        ])
      ]
    }
  });

  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Resources', icon: 'ri-folder-line' },
    { id: 'patient-guides', name: 'Patient Guides', icon: 'ri-user-line' },
    { id: 'family-support', name: 'Family Support', icon: 'ri-group-line' },
    { id: 'insurance', name: 'Insurance & Billing', icon: 'ri-shield-check-line' },
    { id: 'care-planning', name: 'Care Planning', icon: 'ri-calendar-check-line' },
    { id: 'emergency', name: 'Emergency Resources', icon: 'ri-alarm-warning-line' }
  ];

  const resources = [
    {
      category: 'patient-guides',
      title: 'Understanding Home Health Services',
      description: 'A comprehensive guide to what home health services include and what to expect during your care.',
      type: 'PDF Guide',
      downloadUrl: '#',
      icon: 'ri-file-text-line'
    },
    {
      category: 'patient-guides',
      title: 'Preparing for Your First Home Visit',
      description: 'Essential checklist and tips for preparing your home for healthcare visits.',
      type: 'Checklist',
      downloadUrl: '#',
      icon: 'ri-checkbox-line'
    },
    {
      category: 'patient-guides',
      title: 'Medication Management at Home',
      description: 'Best practices for organizing and managing medications safely at home.',
      type: 'Guide',
      downloadUrl: '#',
      icon: 'ri-medicine-bottle-line'
    },
    {
      category: 'family-support',
      title: 'Caregiver Support Guide',
      description: 'Resources and tips for family members providing care and support.',
      type: 'PDF Guide',
      downloadUrl: '#',
      icon: 'ri-heart-line'
    },
    {
      category: 'family-support',
      title: 'Understanding Dementia Care',
      description: "Specialized guidance for families dealing with Alzheimer's and dementia.",
      type: 'Resource Kit',
      downloadUrl: '#',
      icon: 'ri-brain-line'
    },
    {
      category: 'family-support',
      title: 'Respite Care Options',
      description: 'Information about temporary care services to give family caregivers a break.',
      type: 'Information Sheet',
      downloadUrl: '#',
      icon: 'ri-time-line'
    },
    {
      category: 'insurance',
      title: 'Medicare Coverage for Home Health',
      description: 'Detailed explanation of what Medicare covers for home health services.',
      type: 'Coverage Guide',
      downloadUrl: '#',
      icon: 'ri-government-line'
    },
    {
      category: 'insurance',
      title: 'Medicaid Waiver Programs',
      description: 'Information about Nevada Medicaid waiver programs for home and community-based services.',
      type: 'Program Guide',
      downloadUrl: '#',
      icon: 'ri-shield-line'
    },
    {
      category: 'insurance',
      title: 'Private Insurance Navigation',
      description: 'How to work with private insurance for home health coverage.',
      type: 'Guide',
      downloadUrl: '#',
      icon: 'ri-file-shield-line'
    },
    {
      category: 'care-planning',
      title: 'Discharge Planning Checklist',
      description: 'Essential steps for transitioning from hospital to home care.',
      type: 'Checklist',
      downloadUrl: '#',
      icon: 'ri-hospital-line'
    },
    {
      category: 'care-planning',
      title: 'Creating a Care Plan',
      description: 'How to develop an effective care plan with your healthcare team.',
      type: 'Planning Tool',
      downloadUrl: '#',
      icon: 'ri-file-list-line'
    },
    {
      category: 'care-planning',
      title: 'Advanced Directives Guide',
      description: 'Understanding and completing advance healthcare directives.',
      type: 'Legal Guide',
      downloadUrl: '#',
      icon: 'ri-file-paper-line'
    },
    {
      category: 'emergency',
      title: 'Emergency Contact List Template',
      description: 'Printable template for organizing important emergency contacts.',
      type: 'Template',
      downloadUrl: '#',
      icon: 'ri-contacts-line'
    },
    {
      category: 'emergency',
      title: 'When to Call 911 vs. Your Care Team',
      description: 'Guidelines for determining when to seek emergency care.',
      type: 'Quick Reference',
      downloadUrl: '#',
      icon: 'ri-phone-line'
    },
    {
      category: 'emergency',
      title: 'Hospital Bag Checklist',
      description: 'What to pack if you need to go to the hospital.',
      type: 'Checklist',
      downloadUrl: '#',
      icon: 'ri-suitcase-line'
    }
  ];

  const filteredResources = selectedCategory === 'all' 
    ? resources 
    : resources.filter(resource => resource.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-teal-600 to-blue-700 text-white py-24 pt-36 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/12fb784c6cc3ecee6bb3a415728c22b8.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <div className="mb-8">
              <span className="inline-block bg-white/20 text-white px-8 py-4 rounded-full text-xl font-semibold backdrop-blur-sm shadow-lg">
                Healthcare Education & Support
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-8">
              Healthcare Resources &amp; Support
            </h1>
            <p className="text-2xl text-blue-100 max-w-3xl mx-auto">
              Essential guides, tools, and information to help you navigate home healthcare services and make informed decisions about your care.
            </p>
          </div>
        </div>
      </section>

      {/* Resource Categories */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-6">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center px-10 py-5 rounded-full font-semibold transition-colors cursor-pointer whitespace-nowrap text-lg shadow-lg transform hover:scale-105 ${
                  selectedCategory === category.id
                    ? 'bg-blue-600 text-white shadow-xl'
                    : 'bg-white text-gray-700 border-2 border-gray-300 hover:bg-gray-50'
                }`}
              >
                <i className={`${category.icon} mr-3 text-xl`}></i>
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
            {filteredResources.map((resource, index) => (
              <div key={index} className="bg-white rounded-3xl shadow-2xl p-10 hover:shadow-3xl transition-shadow duration-300 border border-gray-100">
                <div className="flex items-start justify-between mb-8">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl flex items-center justify-center shadow-lg">
                    <i className={`${resource.icon} text-blue-600 text-3xl`}></i>
                  </div>
                  <span className="bg-gray-100 text-gray-700 text-base font-medium px-6 py-3 rounded-full">
                    {resource.type}
                  </span>
                </div>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">{resource.title}</h3>
                <p className="text-gray-600 mb-8 text-lg">{resource.description}</p>
                
                <div className="flex items-center justify-between">
                  <a 
                    href={resource.downloadUrl}
                    className="text-blue-600 hover:text-blue-700 font-semibold text-lg transition-colors duration-200"
                  >
                    Download Resource
                  </a>
                  <i className="ri-download-line text-gray-400 text-2xl"></i>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold text-gray-900 mb-8">Frequently Asked Questions</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-xl">
              Find answers to common questions about our services and care options.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto space-y-10">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-3xl shadow-2xl p-10 border border-gray-100">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">{faq.question}</h3>
                <p className="text-gray-600 text-xl leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-blue-600 rounded-2xl p-12 md:p-16 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Need More Information?
            </h2>
            <p className="text-blue-100 mb-10 max-w-2xl mx-auto text-xl">
              Our care coordinators are available to answer your questions and help you access the resources you need.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact">
                <Button
                  variant="secondary"
                  size="lg"
                  className="bg-white text-blue-600 hover:bg-blue-50 whitespace-nowrap"
                >
                  Contact Us
                </Button>
              </Link>
              <Link to="/compare">
                <Button
                  variant="outline"
                  size="lg"
                  className="border-white text-white hover:bg-blue-700 whitespace-nowrap"
                >
                  View Our Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
